import { default as Skeleton } from 'antd/es/skeleton';
import { default as Tabs } from 'antd/es/tabs';
import { type OpenAPI, type OpenAPIV3_1 } from 'openapi-types';

import { EndpointPath } from './EndpointPath';

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CUSTOM_TAB_STYLE } from '../useCustomAntdTheme';
import { enhanceLinkWithToken } from '../utils/enhanceLinkWithToken';
import { renderHtml } from '../utils/renderHtml';
import { AuthHeaders } from './AuthHeaders';
import { Example } from './endpoint/Example';
import { ParametersSection } from './endpoint/ParametersSection';
import { Schema } from './endpoint/Schema';
import { isNotParamReference } from './endpoint/isNotParamReference';
import { useEndpointData } from './endpoint/useEndpointData';

export const EndpointData = ({
  id,
  swaggerDoc
}: {
  id: string;
  swaggerDoc: OpenAPIV3_1.Document | null;
}) => {
  const { endpointDefinitionData, article, loading } = useEndpointData(
    id,
    swaggerDoc
  );

  const [activeResponseData, setActiveResponseData] = useState<
    string | undefined
  >(undefined);

  const adaptedResponseData = useMemo(() => {
    if (endpointDefinitionData) {
      return Object.entries(
        endpointDefinitionData.responses as OpenAPIV3_1.ResponsesObject
      ).map(([statusCode, response]) => {
        return {
          // this is to grab the status code to make it easier to access
          statusCode,
          // this is to extract the payload
          response: response as OpenAPIV3_1.ResponseObject,
          // @ts-expect-error: content is a custom user attribute
          examples: response?.content['application/json']?.examples
        };
      });
    }
  }, [endpointDefinitionData]);

  useEffect(() => {
    if (adaptedResponseData) {
      setActiveResponseData(adaptedResponseData[0].statusCode);
    }
  }, [adaptedResponseData, endpointDefinitionData]);

  if (loading) return <Skeleton active />;

  if (!endpointDefinitionData) return null;

  const endpointTyped = endpointDefinitionData as unknown as OpenAPI.Operation;
  // Filter parameters that are references
  const requestPathParams = endpointTyped.parameters
    ?.filter(isNotParamReference)
    .filter((p) => p.in === 'path');

  const requestHeaderParams = endpointTyped.parameters
    ?.filter(isNotParamReference)
    .filter((p) => p.in === 'header');

  const requestQueryParams = endpointTyped.parameters
    ?.filter(isNotParamReference)
    .filter((p) => p.in === 'query');

  if (!article?.endpoint) return null;

  return (
    <div className="flex flex-col gap-8">
      <div>
        {renderHtml(endpointTyped.description ?? endpointTyped.summary ?? '')}
      </div>
      <EndpointPath
        path={article.endpoint.path ?? ''}
        method={article.endpoint.method}
      />
      <div className="flex flex-wrap gap-4">
        <div className="flex w-full flex-1 flex-col gap-8">
          {(requestPathParams?.length ?? 0 > 0) ? (
            <ParametersSection title="Path params" params={requestPathParams} />
          ) : null}

          <div className="flex flex-col">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-baseline gap-3">
                <p className="text-2xl">Request headers </p>
                <Link
                  to={enhanceLinkWithToken(
                    '/reference/getting-started#http-headers'
                  )}
                  target="_blank"
                >
                  <p className="inpay-link text-inpay-green-primary-1000">
                    Read more
                  </p>
                </Link>
              </div>

              <div className="w-2/3 border-b border-gray-300"></div>
            </div>
            <AuthHeaders />
            {(requestHeaderParams?.length ?? 0 > 0) ? (
              <ParametersSection params={requestHeaderParams} />
            ) : null}
          </div>

          <div className="w-full lg:w-2/3">
            {endpointDefinitionData.requestBody ? (
              <Schema
                input={
                  endpointDefinitionData.requestBody as OpenAPIV3_1.RequestBodyObject
                }
                title="Request Body"
              />
            ) : null}
            {(requestQueryParams?.length ?? 0 > 0) ? (
              <ParametersSection
                title="Query params"
                params={requestQueryParams}
              />
            ) : null}

            {adaptedResponseData ? (
              <Schema
                input={
                  adaptedResponseData.find(
                    (responseData) =>
                      responseData.statusCode === activeResponseData
                  )?.response
                }
                title="Response Schema"
              />
            ) : null}
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col gap-6 lg:w-1/2">
          {endpointDefinitionData.requestBody ? (
            <>
              <Example
                input={
                  endpointDefinitionData.requestBody as OpenAPIV3_1.RequestBodyObject
                }
                title="Request"
              />
            </>
          ) : null}

          <ResponseExamples
            adaptedResponseData={adaptedResponseData}
            onChange={setActiveResponseData}
          />
        </div>
      </div>
    </div>
  );
};

function ResponseExamples({
  adaptedResponseData,
  onChange
}: {
  adaptedResponseData:
    | {
        statusCode: string;
        response: OpenAPIV3_1.ResponseObject;
        examples: {
          [key: string]: { value: unknown };
        };
      }[]
    | undefined;
  onChange: (arg: string) => void;
}) {
  const examples = useMemo(
    () =>
      adaptedResponseData
        ?.map((response) => {
          return Object.entries(response?.examples ?? []).map(
            ([description, example]: [string, { value: unknown }]) => {
              return {
                content: { description, value: example.value },
                statusCode: response.statusCode
              };
            }
          );
        })
        .flat(),
    [adaptedResponseData]
  );

  /**
   * A good swagger file that is auto-generated should contain valid examples
   * good file: https://git.inpay.com/inpay/customer_app/-/blob/CBP-10029-docs-improve-create-order/docs/public/swagger/collection.json
   */
  if (!!examples?.length) {
    return (
      <div className="rounded-md bg-inpay-green-primary-mixes-20%-black text-white">
        <h2 className="p-4">Response</h2>
        <Tabs
          tabBarStyle={CUSTOM_TAB_STYLE}
          items={examples.map((responseData) => ({
            key: `${responseData.statusCode}-${responseData.content.description}`,
            label: `(${responseData.statusCode}) ${responseData.content.description} `,
            children: (
              <Example
                key={responseData.statusCode}
                input={responseData.content.value as OpenAPIV3_1.ResponseObject}
                title=""
                withRadius={false}
              />
            )
          }))}
          onChange={(statusCode) => {
            onChange(statusCode.split('-')?.[0]);
          }}
        />
      </div>
    );
  }
  /**
   * We fallback to this case if our swagger is manually generated
   * example: Check the reconciliation.json
   * manually created file: https://git.inpay.com/inpay/customer_app/-/blob/CBP-10029-docs-improve-create-order/docs/public/swagger/reconciliation.json
   */
  if (adaptedResponseData) {
    return (
      <div className="rounded-md bg-inpay-green-primary-mixes-20%-black text-white">
        <h2 className="p-4">Response</h2>
        <Tabs
          tabBarStyle={CUSTOM_TAB_STYLE}
          items={adaptedResponseData.map((responseData) => ({
            key: responseData.statusCode,
            label: `(${responseData.statusCode}) ${responseData.response.description} `,
            children: (
              <Example
                key={responseData.statusCode}
                input={responseData.response as OpenAPIV3_1.ResponseObject}
                title=""
                withRadius={false}
              />
            )
          }))}
          onChange={(statusCode) => {
            onChange(statusCode);
          }}
        />
      </div>
    );
  }
  return null;
}
